import macro from '../../macros.js';
import vtkCompositeMouseManipulator from './CompositeMouseManipulator.js';

// vtkMouseRangeManipulator methods
// ----------------------------------------------------------------------------

function vtkMouseRangeManipulator(publicAPI, model) {
  // Set our className
  model.classHierarchy.push('vtkMouseRangeManipulator'); // Keep track of delta that is below the value
  // of one step to progressively increment it

  var incrementalDelta = new Map(); // Internal methods
  //-------------------------------------------------------------------------

  function scaleDeltaToRange(listener, normalizedDelta) {
    return normalizedDelta * ((listener.max - listener.min) / (listener.step + 1));
  } //-------------------------------------------------------------------------


  function processDelta(listener, delta) {
    var oldValue = listener.getValue(); // if exponential scroll is enabled, we raise our scale to the
    //  exponent of the net delta of the interaction. The further away
    // the user's cursor is from the start of the interaction, the more
    // their movements will effect the value.

    var scalingFactor = listener.exponentialScroll ? Math.pow(listener.scale, Math.log2(Math.abs(model.interactionNetDelta) + 2)) : listener.scale;
    var newDelta = delta * scalingFactor + incrementalDelta.get(listener);
    var value = oldValue + newDelta; // Compute new value based on step

    var difference = value - listener.min;
    var stepsToDifference = Math.round(difference / listener.step);
    value = listener.min + listener.step * stepsToDifference;
    value = Math.max(value, listener.min);
    value = Math.min(value, listener.max);

    if (value !== oldValue) {
      // Update value
      listener.setValue(value);
      incrementalDelta.set(listener, 0);
    } else if (value === listener.min && newDelta < 0 || value === listener.max && newDelta > 0) {
      // Do not allow incremental delta to go past range
      incrementalDelta.set(listener, 0);
    } else {
      // Store delta for the next iteration
      incrementalDelta.set(listener, newDelta);
    }
  } // Public API methods
  // min:number = minimum allowable value
  // max:number = maximum allowable value
  // step:number = value per step -- smaller = more steps over a given distance, larger = fewer steps over a given distance
  // getValue:fn = function that returns current value
  // setValue:fn = function to set value
  // scale:number = scale value is applied to mouse event to allow users accelerate or decelerate delta without emitting more events
  //-------------------------------------------------------------------------


  publicAPI.setHorizontalListener = function (min, max, step, getValue, setValue) {
    var scale = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 1;
    var exponentialScroll = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
    var getFn = Number.isFinite(getValue) ? function () {
      return getValue;
    } : getValue;
    model.horizontalListener = {
      min: min,
      max: max,
      step: step,
      getValue: getFn,
      setValue: setValue,
      scale: scale,
      exponentialScroll: exponentialScroll
    };
    incrementalDelta.set(model.horizontalListener, 0);
    publicAPI.modified();
  }; //-------------------------------------------------------------------------


  publicAPI.setVerticalListener = function (min, max, step, getValue, setValue) {
    var scale = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 1;
    var exponentialScroll = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
    var getFn = Number.isFinite(getValue) ? function () {
      return getValue;
    } : getValue;
    model.verticalListener = {
      min: min,
      max: max,
      step: step,
      getValue: getFn,
      setValue: setValue,
      scale: scale,
      exponentialScroll: exponentialScroll
    };
    incrementalDelta.set(model.verticalListener, 0);
    publicAPI.modified();
  }; //-------------------------------------------------------------------------


  publicAPI.setScrollListener = function (min, max, step, getValue, setValue) {
    var scale = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 1;
    var exponentialScroll = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
    var getFn = Number.isFinite(getValue) ? function () {
      return getValue;
    } : getValue;
    model.scrollListener = {
      min: min,
      max: max,
      step: step,
      getValue: getFn,
      setValue: setValue,
      scale: scale,
      exponentialScroll: exponentialScroll
    };
    incrementalDelta.set(model.scrollListener, 0);
    publicAPI.modified();
  }; //-------------------------------------------------------------------------


  publicAPI.removeHorizontalListener = function () {
    if (model.verticalListener) {
      incrementalDelta.delete(model.verticalListener);
      delete model.verticalListener;
      publicAPI.modified();
    }
  }; //-------------------------------------------------------------------------


  publicAPI.removeVerticalListener = function () {
    if (model.horizontalListener) {
      incrementalDelta.delete(model.horizontalListener);
      delete model.horizontalListener;
      publicAPI.modified();
    }
  }; //-------------------------------------------------------------------------


  publicAPI.removeScrollListener = function () {
    if (model.scrollListener) {
      incrementalDelta.delete(model.scrollListener);
      delete model.scrollListener;
      publicAPI.modified();
    }
  }; //-------------------------------------------------------------------------


  publicAPI.removeAllListeners = function () {
    publicAPI.removeHorizontalListener();
    publicAPI.removeVerticalListener();
    publicAPI.removeScrollListener();
  }; //-------------------------------------------------------------------------


  publicAPI.onButtonDown = function (interactor, renderer, position) {
    model.previousPosition = position;
    model.interactionNetDelta = 0;
    var glRenderWindow = interactor.getView(); // Ratio is the dom size vs renderwindow size

    var ratio = glRenderWindow.getContainerSize()[0] / glRenderWindow.getSize()[0]; // Get proper pixel range used by viewport in rw size space

    var size = glRenderWindow.getViewportSize(renderer); // rescale size to match mouse event position

    model.containerSize = size.map(function (v) {
      return v * ratio;
    });
  };

  publicAPI.onButtonUp = function (interactor) {
    interactor.exitPointerLock();
  }; //--------------------------------------------------------------------------
  // TODO: at some point, this should perhaps be done in
  // RenderWindowInteractor instead of here.
  // We need to hook into mousemove directly for two reasons:
  // 1. We need to keep receiving mouse move events after the mouse button
  //    is released. This is currently not possible with
  //    vtkInteractorStyleManipulator.
  // 2. Since the mouse is stationary in pointer lock mode, we need the
  //    event.movementX and event.movementY info, which are not currently
  //    passed via interactor.onMouseMove.


  publicAPI.startPointerLockEvent = function (interactor, renderer) {
    var handlePointerLockMove = function handlePointerLockMove(event) {
      publicAPI.onPointerLockMove(interactor, renderer, event);
    };

    document.addEventListener('mousemove', handlePointerLockMove);
    var subscription = null;

    var endInteraction = function endInteraction() {
      var _subscription;

      document.removeEventListener('mousemove', handlePointerLockMove);
      (_subscription = subscription) === null || _subscription === void 0 ? void 0 : _subscription.unsubscribe();
    };

    subscription = interactor === null || interactor === void 0 ? void 0 : interactor.onEndPointerLock(endInteraction);
  };

  publicAPI.onPointerLockMove = function (interactor, renderer, event) {
    // There is a slight delay between the `onEndPointerLock` call
    // and the last `onMouseMove` event, we must make sure the pointer
    // is still locked before we run this logic otherwise we may
    // get a `onMouseMove` call after the pointer has been unlocked.
    if (!interactor.isPointerLocked()) return; // previousPosition could be undefined if for some reason the
    // `startPointerLockEvent` method is called before the `onButtonDown` one.

    if (model.previousPosition == null) return;
    model.previousPosition.x += event.movementX;
    model.previousPosition.y += event.movementY;
    publicAPI.onMouseMove(interactor, renderer, model.previousPosition);
  }; //-------------------------------------------------------------------------


  publicAPI.onMouseMove = function (interactor, renderer, position) {
    if (!model.verticalListener && !model.horizontalListener) {
      return;
    } // We only want to initialize the pointer lock listener
    // after the user starts moving their mouse, this way
    // we don't interfere with other events such as doubleClick,
    // for this reason we don't call this from `onButtonDown`


    if (model.usePointerLock && !interactor.isPointerLocked()) {
      interactor.requestPointerLock();
      publicAPI.startPointerLockEvent(interactor, renderer);
    }

    if (!position) {
      return;
    }

    if (model.horizontalListener) {
      var dxNorm = (position.x - model.previousPosition.x) / model.containerSize[0];
      var dx = scaleDeltaToRange(model.horizontalListener, dxNorm);
      model.interactionNetDelta += dx;
      processDelta(model.horizontalListener, dx);
    }

    if (model.verticalListener) {
      var dyNorm = (position.y - model.previousPosition.y) / model.containerSize[1];
      var dy = scaleDeltaToRange(model.verticalListener, dyNorm);
      model.interactionNetDelta += dy;
      processDelta(model.verticalListener, dy);
    }

    model.previousPosition = position;
  }; //-------------------------------------------------------------------------


  publicAPI.onScroll = function (interactor, renderer, delta) {
    if (!model.scrollListener || !delta) {
      return;
    }

    model.interactionNetDelta += delta * model.scrollListener.step;
    processDelta(model.scrollListener, delta * model.scrollListener.step);
  };

  publicAPI.onStartScroll = function (payload) {
    model.interactionNetDelta = 0;
    publicAPI.onScroll(payload);
  };
} // ----------------------------------------------------------------------------
// Object factory
// ----------------------------------------------------------------------------


var DEFAULT_VALUES = {
  horizontalListener: null,
  verticalListener: null,
  scrollListener: null
}; // ----------------------------------------------------------------------------

function extend(publicAPI, model) {
  var initialValues = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  Object.assign(model, DEFAULT_VALUES, initialValues); // Inheritance

  macro.obj(publicAPI, model);
  vtkCompositeMouseManipulator.extend(publicAPI, model, initialValues); // Create get-set macros

  macro.setGet(publicAPI, model, ['usePointerLock']); // Object specific methods

  vtkMouseRangeManipulator(publicAPI, model);
} // ----------------------------------------------------------------------------

var newInstance = macro.newInstance(extend, 'vtkMouseRangeManipulator'); // ----------------------------------------------------------------------------

var vtkMouseRangeManipulator$1 = {
  newInstance: newInstance,
  extend: extend
};

export { vtkMouseRangeManipulator$1 as default, extend, newInstance };
